import { API_BASE_URL, browserSafeFetch } from "./api-base.ts";

export const PUBLIC_READ_FRESHNESS_MS = 60_000;
const PUBLIC_PATHS = new Set([
  "/api/capabilities", "/api/v2/futures-markets",
  "/api/v2/strategies/library", "/api/v2/strategy-builder/catalog",
]);
type Flight = { controller: AbortController; promise: Promise<unknown>; consumers: number };
const cancelled = () => new DOMException("Request cancelled", "AbortError");

/** Public metadata only. Never stores account data or serves a stale failure. */
export class PublicReadCache {
  private values = new Map<string, { value: unknown; expiresAt: number }>();
  private flights = new Map<string, Flight>();
  private readonly now: () => number;
  constructor(now: () => number = Date.now) { this.now = now; }

  read<T>(key: string, load: (signal: AbortSignal) => Promise<T>, options: {
    signal?: AbortSignal; force?: boolean; freshnessMs?: number; timeoutMs?: number;
  } = {}): Promise<T> {
    if (options.signal?.aborted) return Promise.reject(cancelled());
    const saved = this.values.get(key);
    if (!options.force && saved && saved.expiresAt > this.now()) return Promise.resolve(structuredClone(saved.value) as T);
    if (options.force || (saved && saved.expiresAt <= this.now())) this.values.delete(key);
    let flight = this.flights.get(key);
    if (!flight || flight.controller.signal.aborted) {
      const controller = new AbortController();
      const created: Flight = { controller, consumers: 0, promise: Promise.resolve(undefined) };
      const timeout = setTimeout(() => controller.abort(), options.timeoutMs ?? 20_000);
      const pending = new Promise<T>((resolve, reject) => {
        const abort = () => reject(cancelled());
        controller.signal.addEventListener("abort", abort, { once: true });
        try {
          Promise.resolve(load(controller.signal)).then(resolve, reject)
            .finally(() => controller.signal.removeEventListener("abort", abort));
        } catch (error) { controller.signal.removeEventListener("abort", abort); reject(error); }
      });
      created.promise = pending.then((value) => {
        if (!controller.signal.aborted) {
          this.values.delete(key);
          this.values.set(key, { value: structuredClone(value), expiresAt: this.now() + (options.freshnessMs ?? PUBLIC_READ_FRESHNESS_MS) });
          while (this.values.size > 64) this.values.delete(this.values.keys().next().value!);
        }
        return value;
      }).finally(() => {
        clearTimeout(timeout);
        if (this.flights.get(key) === created) this.flights.delete(key);
      });
      flight = created; this.flights.set(key, flight);
    }
    const joined = flight; joined.consumers += 1;
    return new Promise<T>((resolve, reject) => {
      let released = false;
      const release = () => {
        if (released) return;
        released = true; options.signal?.removeEventListener("abort", abort); joined.consumers -= 1;
        // React can hand a request to a newly mounted consumer in the same turn.
        queueMicrotask(() => { if (!joined.consumers && this.flights.get(key) === joined) joined.controller.abort(); });
      };
      const abort = () => { release(); reject(cancelled()); };
      options.signal?.addEventListener("abort", abort, { once: true });
      joined.promise.then((value) => {
        if (!released) { release(); resolve(structuredClone(value) as T); }
      }, (error: unknown) => { if (!released) { release(); reject(error); } });
    });
  }
}

const caches = new WeakMap<typeof fetch, PublicReadCache>();
export function sharedPublicRead<T>(path: string, locale: string, load: (signal: AbortSignal) => Promise<T>, options: {
  baseURL?: string; fetcher?: typeof fetch; signal?: AbortSignal; force?: boolean;
} = {}): Promise<T> {
  const base = new URL(options.baseURL ?? API_BASE_URL), target = new URL(path, `${base.origin}/`);
  if (target.origin !== base.origin || !PUBLIC_PATHS.has(target.pathname)) throw new Error("private_read_cannot_be_cached");
  const fetcher = options.fetcher ?? browserSafeFetch;
  const identity = fetcher === browserSafeFetch ? globalThis.fetch : fetcher;
  let cache = caches.get(identity);
  if (!cache) { cache = new PublicReadCache(); caches.set(identity, cache); }
  return cache.read(JSON.stringify([base.origin, target.pathname, target.search, locale]), load, options);
}
